import { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import { IconX } from '@tabler/icons-react';

import { Button, IconButton, Input } from 'ui';

const ConnectDtdModal = ({ open, onClose, onConnect }) => {
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (loading) return;
    setPublicKey('');
    setPrivateKey('');
    setError('');
    onClose();
  };

  const handleConnect = async () => {
    if (!publicKey.trim() || !privateKey.trim()) {
      setError('Both public key and private key are required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onConnect({ publicKey: publicKey.trim(), privateKey: privateKey.trim() });
      handleClose();
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to connect. Please check your keys and try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
    >
      <div className="space-y-6 p-6">
        <div className="relative flex">
          <h3 className="text-h6 font-semibold">Connect Double the Donation</h3>

          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
            disabled={loading}
          />
        </div>

        <p className="text-sm text-gray-500">
          Enter your public and private keys to connect your account. These can be found in your Double the Donation
          dashboard under Settings - API Keys.
        </p>

        <Input
          id="dtd-public-key"
          label="Public Key"
          value={publicKey}
          onChange={(e) => setPublicKey(e.target.value)}
          placeholder="Enter your public key"
          required
          size="xl"
        />

        <Input
          id="dtd-private-key"
          label="Private Key"
          value={privateKey}
          onChange={(e) => setPrivateKey(e.target.value)}
          placeholder="Enter your private key"
          required
          size="xl"
          type="password"
        />

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex justify-end space-x-3">
          <Button title="Cancel" onClick={handleClose} disabled={loading} />

          <Button title="Connect" color="primary" onClick={handleConnect} loading={loading} disabled={loading} />
        </div>
      </div>
    </Dialog>
  );
};

ConnectDtdModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConnect: PropTypes.func.isRequired,
};

export default ConnectDtdModal;
